import {type Observable, Subject, type Subscription} from 'rxjs';
import * as THREE from 'three/webgpu';
import type {Viewer} from '../viewer/Viewer';
import {type RawPointerEventPayload, RawPointerEventType} from '../viewer/moudles/EventManager';
import type {CameraManager} from './CameraManager';
import {StandardViewType, ViewOrientationElementMesh, ViewOrientationMesh} from "./view-orientation";

/**
 * 视图定向交互控制器。
 *
 * 设计说明：
 * 1. 该类负责场景内视图定向器的 hover、click 与空格快捷打开；
 * 2. 命中后统一通过 defaultPrevented 抢占场景普通选择链；
 * 3. 相机切换与激活态计算仍交给 CameraManager 统一收口。
 */
export class ViewOrientationInteractionController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 相机管理器实例。 */
    private readonly cameraManager: CameraManager;

    /** 射线拾取器。 */
    private readonly raycaster: THREE.Raycaster = new THREE.Raycaster();

    /** 鼠标标准化设备坐标缓存。 */
    private readonly pointerNdc: THREE.Vector2 = new THREE.Vector2();

    /** 仅在场景内视图定向器可见时存在的指针事件订阅集合。 */
    private readonly pointerSubscriptions: Subscription[] = [];

    /** 当前是否已经启用场景内视图定向器指针交互。 */
    private pointerInteractionEnabled: boolean = false;

    /** 当前由控制器维护的 Hover 网格，用于阻止重复视觉刷新和事件广播。 */
    private hoveredMesh: ViewOrientationElementMesh | null = null;

    /** 是否已启动。 */
    private started: boolean = false;

    /** hover viewType 变更事件主题。 */
    private readonly hoverChangeSubject = new Subject<StandardViewType | null>();

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     * @param cameraManager 相机管理器实例。
     */
    public constructor(viewer: Viewer, cameraManager: CameraManager) {
        this.viewer = viewer;
        this.cameraManager = cameraManager;
        // 视角定位 mesh 使用 layer 31，raycaster 必须启用该 layer 才能命中
        this.raycaster.layers.enable(ViewOrientationMesh.MESH_LAYER);
    }

    /**
     * 读取 hover viewType 变更事件流。
     *
     * @returns Observable，每次 hover 命中 mesh 发生变化时推送最新 viewType（null 表示无命中）。
     */
    public observeHoverChange(): Observable<StandardViewType | null> {
        return this.hoverChangeSubject.asObservable();
    }

    /**
     * 启动交互监听。
     */
    public start(): void {
        if (this.started) {
            return;
        }

        window.addEventListener('keydown', this.handleKeyDown);
        this.started = true;
        // 空格键需要在关闭态保持可用，指针事件则只在场景内定向器实际显示时订阅。
        this.setPointerInteractionEnabled(this.cameraManager.isViewOrientationMeshVisible());
    }

    /**
     * 按场景内视图定向器可见状态启用或停用指针交互。
     *
     * 设计说明：
     * 1. 关闭状态不订阅 Viewer 原始指针流，草图拖动、框选和相机操作不会进入当前控制器；
     * 2. 重复设置相同状态保持幂等，避免多次打开累积重复订阅；
     * 3. 停用时只清理一次控制器维护的 Hover，主场景渲染由 CameraManager 的显隐提交统一负责。
     *
     * @param enabled 是否启用场景内视图定向器指针交互。
     */
    public setPointerInteractionEnabled(enabled: boolean): void {
        if (!this.started || this.pointerInteractionEnabled === enabled) {
            return;
        }

        this.pointerInteractionEnabled = enabled;
        if (enabled) {
            this.subscribePointerEvents();
            return;
        }

        this.unsubscribePointerEvents();
        this.resetHoveredMeshWithoutRender();
    }

    /**
     * 订阅场景内视图定向器所需的原始指针事件。
     */
    private subscribePointerEvents(): void {
        const mouseDownSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.MouseDown)?.subscribe((payload) => {
            this.handleMouseDown(payload);
        });
        const mouseMoveSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.MouseMove)?.subscribe((payload) => {
            this.handleMouseMove(payload);
        });
        const clickSubscription = this.viewer.observeRawPointerEvent(RawPointerEventType.Click)?.subscribe((payload) => {
            this.handleClick(payload);
        });

        if (mouseDownSubscription) {
            this.pointerSubscriptions.push(mouseDownSubscription);
        }
        if (mouseMoveSubscription) {
            this.pointerSubscriptions.push(mouseMoveSubscription);
        }
        if (clickSubscription) {
            this.pointerSubscriptions.push(clickSubscription);
        }
    }

    /**
     * 取消当前场景内视图定向器的全部原始指针订阅。
     */
    private unsubscribePointerEvents(): void {
        this.pointerSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.pointerSubscriptions.length = 0;
    }

    /**
     * 在不主动请求主场景渲染的前提下清理控制器 Hover 状态。
     *
     * CameraManager 关闭定向器时会统一提交一次显隐渲染；这里禁止重复请求渲染。
     */
    private resetHoveredMeshWithoutRender(): void {
        if (this.hoveredMesh === null) {
            return;
        }

        this.hoveredMesh = null;
        this.cameraManager.viewOrientationMesh?.setHoveredMesh(null);
        this.hoverChangeSubject.next(null);
    }

    /**
     * 释放交互监听资源。
     */
    public dispose(): void {
        window.removeEventListener('keydown', this.handleKeyDown);
        this.setPointerInteractionEnabled(false);
        this.resetHoveredMeshWithoutRender();
        this.started = false;
        // 完成 Subject，通知下游订阅者资源已释放
        this.hoverChangeSubject.complete();
    }

    /**
     * 处理鼠标按下。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== 0 || payload.nativeEvent.defaultPrevented) {
            return;
        }

        const pickedMesh = this.pickViewOrientationMesh(payload);
        if (!pickedMesh) {
            return;
        }

        payload.nativeEvent.preventDefault();
        payload.nativeEvent.stopPropagation();
    }

    /**
     * 处理鼠标移动。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleMouseMove(payload: RawPointerEventPayload): void {
        if (payload.nativeEvent.defaultPrevented) {
            return;
        }

        if (payload.buttons !== 0) {
            this.updateHoveredMesh(null);
            return;
        }

        const pickedMesh = this.pickViewOrientationMesh(payload);
        this.updateHoveredMesh(pickedMesh);

        if (!pickedMesh) {
            return;
        }

        payload.nativeEvent.preventDefault();
        payload.nativeEvent.stopPropagation();
    }

    /**
     * 处理鼠标单击。
     *
     * @param payload 原始鼠标事件载荷。
     */
    private handleClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0 || payload.nativeEvent.defaultPrevented) {
            return;
        }

        const pickedMesh = this.pickViewOrientationMesh(payload);
        if (!pickedMesh) {
            if (this.cameraManager.isViewOrientationFocusModeActive()) {
                payload.nativeEvent.preventDefault();
                payload.nativeEvent.stopPropagation();
                this.cameraManager.exitViewOrientationFocusMode({ restoreCamera: true });
                this.updateHoveredMesh(null);
            }
            return;
        }

        payload.nativeEvent.preventDefault();
        payload.nativeEvent.stopPropagation();
        this.cameraManager.setStandardView(pickedMesh.viewType);
        this.cameraManager.exitViewOrientationFocusMode({ restoreCamera: false });
        this.updateHoveredMesh(null);
    }


    /**
     * 处理键盘按下。
     *
     * 设计说明：
     * 1. 空格快捷键只负责打开视图定向聚焦态；
     * 2. 若已经处于聚焦态，则不重复进入；
     * 3. 监听注册与释放均收口在当前控制器，避免宿主 UI 重复接管。
     *
     * @param event 键盘事件。
     */
    private readonly handleKeyDown = (event: KeyboardEvent): void => {
        if (event.code !== 'Space' || event.defaultPrevented) {
            return;
        }

        if (this.isEditableKeyboardEvent(event)) {
            return;
        }

        if (this.cameraManager.isViewOrientationFocusModeActive()) {
            return;
        }

        event.preventDefault();
        this.cameraManager.enterViewOrientationFocusMode();
    };

    /**
     * 判断键盘事件是否来自可编辑控件。
     *
     * 设计说明：
     * 1. 视图定向空格快捷键属于视口全局快捷键，不应抢占输入框、文本域和富文本编辑区；
     * 2. Naive UI 等组件可能在输入控件外包裹多层元素，因此优先检查事件路径，再回退到 target；
     * 3. 该判断收口在 cad-core，避免每个宿主 UI 都重复为同一个全局快捷键补防护。
     *
     * @param event 键盘事件。
     * @returns 事件来自可编辑控件时返回 true。
     */
    private isEditableKeyboardEvent(event: KeyboardEvent): boolean {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

        if (path.some((target) => this.isEditableKeyboardEventTarget(target))) {
            return true;
        }

        return this.isEditableKeyboardEventTarget(event.target);
    }

    /**
     * 判断单个事件路径节点是否为可编辑控件。
     *
     * @param target 事件路径节点。
     * @returns 节点为可编辑控件或处于可编辑控件内部时返回 true。
     */
    private isEditableKeyboardEventTarget(target: EventTarget | null): boolean {
        if (!(target instanceof HTMLElement)) {
            return false;
        }

        if (target.isContentEditable) {
            return true;
        }

        return target.closest('input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]') !== null;
    }


    /**
     * 更新当前 hover 命中 mesh。
     *
     * @param mesh 当前 hover 命中的 mesh。
     */
    private updateHoveredMesh(mesh: ViewOrientationElementMesh | null): void {
        const viewOrientationMesh = this.cameraManager.viewOrientationMesh;
        if (!viewOrientationMesh || this.hoveredMesh === mesh) {
            return;
        }

        this.hoveredMesh = mesh;
        viewOrientationMesh.setHoveredMesh(mesh);
        // 命中抢占和通知仍同步执行；辅助定向器的颜色变化与主场景共用帧末提交。
        this.viewer.requestRender();
        // 通知外部订阅者当前 hover viewType 发生变化
        this.hoverChangeSubject.next(mesh?.viewType ?? null);
    }

    /**
     * 执行视图定向器拾取。
     *
     * @param payload 原始鼠标事件载荷。
     * @returns 命中的元素 mesh；未命中时返回 null。
     */
    private pickViewOrientationMesh(payload: RawPointerEventPayload): ViewOrientationElementMesh | null {
        const viewOrientationMesh = this.cameraManager.viewOrientationMesh;
        const camera = this.viewer.getCamera();
        const container = this.viewer.getContainer();
        if (!viewOrientationMesh || !camera || !container || !viewOrientationMesh.visible) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        this.pointerNdc.set(
            ((payload.clientX - rect.left) / rect.width) * 2 - 1,
            -(((payload.clientY - rect.top) / rect.height) * 2 - 1),
        );

        viewOrientationMesh.updateMatrixWorld(true);
        this.raycaster.setFromCamera(this.pointerNdc, camera);
        this.raycaster.firstHitOnly = true;
        const intersections = this.raycaster.intersectObjects(Array.from(viewOrientationMesh.getAllMeshes()), false);
        const picked = intersections.find((intersection) => intersection.object instanceof ViewOrientationElementMesh);
        if (!picked) {
            return null;
        }

        return picked.object as ViewOrientationElementMesh;
    }
}
